import React, { useState, useEffect, useRef } from 'react';
import './MobileDevConsole.css';

const MobileDevConsole = () => {
    const [activeTab, setActiveTab] = useState('chat');
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [systemStatus, setSystemStatus] = useState(null);
    const [files, setFiles] = useState([]);
    const [currentPath, setCurrentPath] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileContent, setFileContent] = useState('');
    const [sqlQuery, setSqlQuery] = useState('');
    const [sqlResults, setSqlResults] = useState(null);
    const [logs, setLogs] = useState([]);
    const [selectedLogType, setSelectedLogType] = useState('backend');
    
    const chatEndRef = useRef(null);
    const fileTextareaRef = useRef(null);

    // Auto-scroll chat to bottom
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages]);

    // Load system status on mount
    useEffect(() => {
        loadSystemStatus();
    }, []);

    const apiCall = async (endpoint, options = {}) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/mobile-dev${endpoint}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    ...options.headers
                },
                ...options
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('API call failed:', error);
            throw error;
        }
    };

    // Chat functionality
    const sendChatMessage = async () => {
        if (!chatInput.trim() || isLoading) return;

        const userMessage = { type: 'user', message: chatInput, timestamp: new Date() };
        setChatMessages(prev => [...prev, userMessage]);
        
        const currentInput = chatInput;
        setChatInput('');
        setIsLoading(true);

        try {
            const response = await apiCall('/chat', {
                method: 'POST',
                body: JSON.stringify({ 
                    message: currentInput,
                    context: 'mobile-dev-console'
                })
            });

            const aiMessage = { 
                type: 'ai', 
                message: response.response.message, 
                timestamp: new Date() 
            };
            setChatMessages(prev => [...prev, aiMessage]);
        } catch (error) {
            const errorMessage = { 
                type: 'error', 
                message: `Error: ${error.message}`, 
                timestamp: new Date() 
            };
            setChatMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    // System status
    const loadSystemStatus = async () => {
        try {
            const response = await apiCall('/status');
            setSystemStatus(response.status);
        } catch (error) {
            console.error('Failed to load system status:', error);
        }
    };

    // File operations
    const loadFiles = async (path = '') => {
        try {
            const response = await apiCall(`/files?path=${encodeURIComponent(path)}`);
            if (response.type === 'directory') {
                setFiles(response.contents);
                setCurrentPath(response.path);
                setSelectedFile(null);
                setFileContent('');
            } else {
                setSelectedFile(response.path);
                setFileContent(response.content);
            }
        } catch (error) {
            console.error('Failed to load files:', error);
        }
    };

    const saveFile = async () => {
        if (!selectedFile) return;

        try {
            await apiCall('/files', {
                method: 'POST',
                body: JSON.stringify({
                    path: selectedFile,
                    content: fileContent
                })
            });
            alert('File saved successfully!');
        } catch (error) {
            alert(`Failed to save file: ${error.message}`);
        }
    };

    // Database operations
    const executeSqlQuery = async () => {
        if (!sqlQuery.trim()) return;

        try {
            setIsLoading(true);
            const response = await apiCall('/database', {
                method: 'POST',
                body: JSON.stringify({ query: sqlQuery })
            });
            setSqlResults(response.result);
        } catch (error) {
            setSqlResults({ error: error.message });
        } finally {
            setIsLoading(false);
        }
    };

    // Deployment operations
    const deploy = async (operation) => {
        try {
            setIsLoading(true);
            const response = await apiCall('/deploy', {
                method: 'POST',
                body: JSON.stringify({ operation })
            });
            
            alert(`Deployment ${operation} completed successfully!`);
            console.log('Deployment result:', response);
        } catch (error) {
            alert(`Deployment failed: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Log operations
    const loadLogs = async (type = selectedLogType) => {
        try {
            const response = await apiCall(`/logs?type=${type}&lines=100`);
            setLogs(response.lines);
        } catch (error) {
            console.error('Failed to load logs:', error);
            setLogs([`Error loading logs: ${error.message}`]);
        }
    };

    const renderChatTab = () => (
        <div className="chat-container">
            <div className="chat-messages">
                {chatMessages.map((msg, index) => (
                    <div key={index} className={`message ${msg.type}`}>
                        <div className="message-content">{msg.message}</div>
                        <div className="message-time">
                            {msg.timestamp.toLocaleTimeString()}
                        </div>
                    </div>
                ))}
                {isLoading && (
                    <div className="message ai loading">
                        <div className="message-content">Thinking...</div>
                    </div>
                )}
                <div ref={chatEndRef} />
            </div>
            <div className="chat-input">
                <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                    placeholder="Ask me about implementations, fixes, or new features..."
                    disabled={isLoading}
                />
                <button onClick={sendChatMessage} disabled={isLoading}>
                    Send
                </button>
            </div>
        </div>
    );

    const renderFilesTab = () => (
        <div className="files-container">
            <div className="file-navigation">
                <div className="current-path">
                    Path: /{currentPath}
                    {currentPath && (
                        <button onClick={() => loadFiles(currentPath.split('/').slice(0, -1).join('/'))}>
                            ← Back
                        </button>
                    )}
                </div>
                <button onClick={() => loadFiles(currentPath)}>Refresh</button>
            </div>
            
            {!selectedFile ? (
                <div className="file-list">
                    {files.map((file, index) => (
                        <div 
                            key={index} 
                            className={`file-item ${file.type}`}
                            onClick={() => file.type === 'directory' ? loadFiles(file.path) : loadFiles(file.path)}
                        >
                            <span className="file-icon">
                                {file.type === 'directory' ? '📁' : '📄'}
                            </span>
                            <span className="file-name">{file.name}</span>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="file-editor">
                    <div className="editor-header">
                        <span>Editing: {selectedFile}</span>
                        <div className="editor-actions">
                            <button onClick={saveFile}>Save</button>
                            <button onClick={() => setSelectedFile(null)}>Close</button>
                        </div>
                    </div>
                    <textarea
                        ref={fileTextareaRef}
                        value={fileContent}
                        onChange={(e) => setFileContent(e.target.value)}
                        className="code-editor"
                        spellCheck={false}
                    />
                </div>
            )}
        </div>
    );

    const renderDatabaseTab = () => (
        <div className="database-container">
            <div className="sql-editor">
                <textarea
                    value={sqlQuery}
                    onChange={(e) => setSqlQuery(e.target.value)}
                    placeholder="Enter SQL query..."
                    className="sql-input"
                />
                <button onClick={executeSqlQuery} disabled={isLoading}>
                    Execute Query
                </button>
            </div>
            
            {sqlResults && (
                <div className="sql-results">
                    {sqlResults.error ? (
                        <div className="error">Error: {sqlResults.error}</div>
                    ) : (
                        <div>
                            <div className="result-info">
                                Rows: {sqlResults.rowCount} | 
                                Time: {new Date(sqlResults.timestamp).toLocaleTimeString()}
                            </div>
                            {sqlResults.rows.length > 0 && (
                                <div className="result-table">
                                    <table>
                                        <thead>
                                            <tr>
                                                {sqlResults.fields.map(field => (
                                                    <th key={field}>{field}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sqlResults.rows.slice(0, 50).map((row, index) => (
                                                <tr key={index}>
                                                    {sqlResults.fields.map(field => (
                                                        <td key={field}>
                                                            {JSON.stringify(row[field])}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    const renderDeployTab = () => (
        <div className="deploy-container">
            <div className="deploy-actions">
                <button 
                    onClick={() => deploy('build-frontend')} 
                    disabled={isLoading}
                    className="deploy-btn"
                >
                    Build Frontend
                </button>
                <button 
                    onClick={() => deploy('restart-backend')} 
                    disabled={isLoading}
                    className="deploy-btn"
                >
                    Restart Backend
                </button>
                <button 
                    onClick={() => deploy('reload-nginx')} 
                    disabled={isLoading}
                    className="deploy-btn"
                >
                    Reload Nginx
                </button>
                <button 
                    onClick={() => deploy('full-deploy')} 
                    disabled={isLoading}
                    className="deploy-btn primary"
                >
                    Full Deploy
                </button>
            </div>

            {systemStatus && (
                <div className="system-status">
                    <h3>System Status</h3>
                    <div className="status-grid">
                        <div className="status-item">
                            <strong>PM2 Processes:</strong>
                            {systemStatus.pm2Processes.map(proc => (
                                <div key={proc.name} className={`process ${proc.status}`}>
                                    {proc.name}: {proc.status} (CPU: {proc.cpu}%, Memory: {Math.round(proc.memory / 1024 / 1024)}MB)
                                </div>
                            ))}
                        </div>
                        <div className="status-item">
                            <strong>Disk Usage:</strong>
                            <pre>{systemStatus.diskUsage}</pre>
                        </div>
                    </div>
                    <button onClick={loadSystemStatus}>Refresh Status</button>
                </div>
            )}
        </div>
    );

    const renderLogsTab = () => (
        <div className="logs-container">
            <div className="log-controls">
                <select 
                    value={selectedLogType} 
                    onChange={(e) => setSelectedLogType(e.target.value)}
                >
                    <option value="backend">Backend</option>
                    <option value="pm2">PM2</option>
                    <option value="nginx-access">Nginx Access</option>
                    <option value="nginx-error">Nginx Error</option>
                    <option value="mobile-dev-chat">Mobile Dev Chat</option>
                </select>
                <button onClick={() => loadLogs(selectedLogType)}>Load Logs</button>
            </div>
            
            <div className="log-output">
                {logs.map((line, index) => (
                    <div key={index} className="log-line">
                        {line}
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="mobile-dev-console">
            <div className="console-header">
                <h1>🛠️ Mobile Dev Console</h1>
                <div className="status-indicator">
                    <span className="status-dot online"></span>
                    Connected
                </div>
            </div>

            <div className="console-tabs">
                <button 
                    className={activeTab === 'chat' ? 'active' : ''}
                    onClick={() => setActiveTab('chat')}
                >
                    💬 Chat
                </button>
                <button 
                    className={activeTab === 'files' ? 'active' : ''}
                    onClick={() => {
                        setActiveTab('files');
                        if (files.length === 0) loadFiles();
                    }}
                >
                    📁 Files
                </button>
                <button 
                    className={activeTab === 'database' ? 'active' : ''}
                    onClick={() => setActiveTab('database')}
                >
                    🗄️ Database
                </button>
                <button 
                    className={activeTab === 'deploy' ? 'active' : ''}
                    onClick={() => setActiveTab('deploy')}
                >
                    🚀 Deploy
                </button>
                <button 
                    className={activeTab === 'logs' ? 'active' : ''}
                    onClick={() => {
                        setActiveTab('logs');
                        loadLogs();
                    }}
                >
                    📋 Logs
                </button>
            </div>

            <div className="console-content">
                {activeTab === 'chat' && renderChatTab()}
                {activeTab === 'files' && renderFilesTab()}
                {activeTab === 'database' && renderDatabaseTab()}
                {activeTab === 'deploy' && renderDeployTab()}
                {activeTab === 'logs' && renderLogsTab()}
            </div>
        </div>
    );
};

export default MobileDevConsole;
